/**
 * Shared Stream Chat client — keeps unread badge + desktop notifications alive
 * across routes without re-mounting on every /chat visit.
 * Connected for the authenticated session so nav badges stay live.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { StreamChat, type Event as StreamEvent } from 'stream-chat';
import { useLocation } from 'react-router';
import { useAuthStore } from '../../store/auth.js';
import { ApiRequestError } from '../../lib/api.js';
import { fetchChatToken } from './api.js';
import {
  isInQuietHours,
  loadChatPrefs,
  saveChatPrefs,
  shouldNotify,
  type ChatPrefs,
} from './prefs.js';

type ChatClientContextValue = {
  client: StreamChat | null;
  ready: boolean;
  error: string | null;
  unreadCount: number;
  prefs: ChatPrefs;
  setPrefs: (next: ChatPrefs) => void;
  requestNotifyPermission: () => Promise<NotificationPermission | 'unsupported'>;
};

const ChatClientContext = createContext<ChatClientContextValue | null>(null);

function playNotifyChime(): void {
  try {
    const Ctx =
      window.AudioContext
      || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.value = 0.04;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
    osc.stop(ctx.currentTime + 0.26);
    void ctx.close().catch(() => undefined);
  } catch {
    // ignore autoplay blocks
  }
}

function readUnread(client: StreamChat): number {
  const user = client.user as { total_unread_count?: number } | undefined;
  const n = user?.total_unread_count;
  return typeof n === 'number' && n > 0 ? n : 0;
}

export function ChatClientProvider({ children }: { children: ReactNode }) {
  const authUser = useAuthStore((s) => s.actualUser);
  const location = useLocation();
  const [client, setClient] = useState<StreamChat | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [prefs, setPrefsState] = useState<ChatPrefs>(() => loadChatPrefs());
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  const setPrefs = useCallback((next: ChatPrefs) => {
    setPrefsState(next);
    saveChatPrefs(next);
  }, []);

  useEffect(() => {
    const onPrefs = (e: Event) => {
      const detail = (e as CustomEvent<ChatPrefs>).detail;
      if (detail) setPrefsState(detail);
    };
    window.addEventListener('vibe-chat-prefs', onPrefs);
    return () => window.removeEventListener('vibe-chat-prefs', onPrefs);
  }, []);

  useEffect(() => {
    if (!authUser) {
      setClient(null);
      setReady(false);
      setUnreadCount(0);
      setError(null);
      return;
    }

    let cancelled = false;
    let chatClient: StreamChat | null = null;
    let cleanupListeners: (() => void) | null = null;

    void (async () => {
      try {
        const boot = await fetchChatToken();
        if (cancelled) return;

        chatClient = StreamChat.getInstance(boot.apiKey);
        if (chatClient.userID && chatClient.userID !== boot.userId) {
          await chatClient.disconnectUser();
        }
        if (cancelled) return;

        if (!chatClient.userID) {
          await chatClient.connectUser(
            {
              id: boot.userId,
              name: boot.name,
              image: boot.image ?? undefined,
            },
            async () => {
              const res = await fetchChatToken();
              return res.token;
            },
          );
        }
        if (cancelled) {
          await chatClient.disconnectUser();
          return;
        }

        setClient(chatClient);
        setReady(true);
        setError(null);
        setUnreadCount(readUnread(chatClient));

        const refreshUnread = () => {
          if (chatClient) setUnreadCount(readUnread(chatClient));
        };

        const onNotification = (event: StreamEvent) => {
          refreshUnread();
          if (pathRef.current.startsWith('/chat')) return;
          const prefsNow = prefsRef.current;
          if (!shouldNotify(prefsNow)) return;
          if (typeof Notification === 'undefined') return;
          if (Notification.permission !== 'granted') return;

          const msg = event.message;
          const title = msg?.user?.name ? String(msg.user.name) : 'New chat message';
          const body =
            typeof msg?.text === 'string' && msg.text.trim()
              ? msg.text.trim().slice(0, 140)
              : 'Open Chat to read the message';

          try {
            const n = new Notification(title, {
              body,
              tag: `vibe-chat-${event.cid ?? 'msg'}`,
            });
            n.onclick = () => {
              window.focus();
              window.location.assign('/chat');
              n.close();
            };
          } catch {
            // ignore
          }

          if (prefsNow.sound && !isInQuietHours(prefsNow)) {
            playNotifyChime();
          }
        };

        chatClient.on('notification.message_new', onNotification);
        chatClient.on('notification.mark_read', refreshUnread);
        chatClient.on('message.new', refreshUnread);
        chatClient.on('connection.recovered', refreshUnread);

        cleanupListeners = () => {
          chatClient?.off('notification.message_new', onNotification);
          chatClient?.off('notification.mark_read', refreshUnread);
          chatClient?.off('message.new', refreshUnread);
          chatClient?.off('connection.recovered', refreshUnread);
        };
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof ApiRequestError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Chat unavailable';
        setError(message);
        setReady(false);
        setClient(null);
      }
    })();

    return () => {
      cancelled = true;
      cleanupListeners?.();
      if (chatClient) {
        void chatClient.disconnectUser().catch(() => undefined);
      }
      setClient(null);
      setReady(false);
    };
  }, [authUser?.id]);

  const requestNotifyPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return 'unsupported' as const;
    if (Notification.permission === 'granted') return 'granted';
    if (Notification.permission === 'denied') return 'denied';
    return Notification.requestPermission();
  }, []);

  const value = useMemo<ChatClientContextValue>(
    () => ({
      client,
      ready,
      error,
      unreadCount,
      prefs,
      setPrefs,
      requestNotifyPermission,
    }),
    [client, ready, error, unreadCount, prefs, setPrefs, requestNotifyPermission],
  );

  return (
    <ChatClientContext.Provider value={value}>
      {children}
    </ChatClientContext.Provider>
  );
}

export function useChatClient(): ChatClientContextValue {
  const ctx = useContext(ChatClientContext);
  if (!ctx) {
    throw new Error('useChatClient must be used within ChatClientProvider');
  }
  return ctx;
}

export function useChatUnreadCount(): number {
  const ctx = useContext(ChatClientContext);
  return ctx?.unreadCount ?? 0;
}
